"use client";

import { useCallback, useMemo, useState } from "react";
import { Chess, type PieceSymbol, type Square } from "chess.js";

export type Orientation = "white" | "black";
export type Movable = "white" | "black" | "both" | "none";
export type MoveIntent = { from: Square; to: Square; promotion?: PieceSymbol };
export type Arrow = { from: Square; to: Square; color?: string };

export const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

export const PIECE_LABEL: Record<PieceSymbol, string> = {
  k: "king",
  q: "queen",
  r: "rook",
  b: "bishop",
  n: "knight",
  p: "pawn",
};

/** Squares in display order (top-left first) for the given orientation. */
export function squaresFor(orientation: Orientation): Square[] {
  const out: Square[] = [];
  for (let r = 0; r < 8; r++)
    for (let f = 0; f < 8; f++) {
      const file = orientation === "white" ? f : 7 - f;
      const rank = orientation === "white" ? 8 - r : r + 1;
      out.push(`${FILES[file]}${rank}` as Square);
    }
  return out;
}

export function isLightSquare(sq: Square): boolean {
  const file = sq.charCodeAt(0) - 97;
  const rank = Number(sq[1]) - 1;
  return (file + rank) % 2 === 1;
}

/**
 * Click-to-move state machine shared by the 2D and 3D boards: first click selects
 * one of your pieces, the second click on a highlighted square moves. Promotions
 * pause for a piece choice.
 */
export function useBoardInteraction(fen: string, movable: Movable, onMove?: (m: MoveIntent) => boolean | void) {
  const [selected, setSelected] = useState<Square | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{ from: Square; to: Square } | null>(null);

  const chess = useMemo(() => {
    try {
      return new Chess(fen);
    } catch {
      return new Chess();
    }
  }, [fen]);

  const turn = chess.turn();
  const canMoveColor = useCallback(
    (color: "w" | "b") =>
      movable === "both" || (movable === "white" && color === "w") || (movable === "black" && color === "b"),
    [movable],
  );

  const targets = useMemo(() => {
    if (!selected) return new Map<Square, { capture: boolean }>();
    const m = new Map<Square, { capture: boolean }>();
    for (const mv of chess.moves({ square: selected, verbose: true })) m.set(mv.to, { capture: !!mv.captured });
    return m;
  }, [chess, selected]);

  const [prevFen, setPrevFen] = useState(fen);
  if (prevFen !== fen) {
    // Position changed from outside (bot move, navigation): drop any selection.
    setPrevFen(fen);
    setSelected(null);
    setPendingPromotion(null);
  }

  const canSelect = useCallback(
    (sq: Square) => {
      if (movable === "none") return false;
      const p = chess.get(sq);
      return !!p && p.color === turn && canMoveColor(p.color);
    },
    [chess, movable, turn, canMoveColor],
  );

  const tryMove = useCallback(
    (from: Square, to: Square): boolean => {
      const legal = chess.moves({ square: from, verbose: true }).filter((m) => m.to === to);
      if (legal.length === 0) return false;
      if (legal.some((m) => m.promotion)) {
        setPendingPromotion({ from, to });
        return true;
      }
      const accepted = onMove?.({ from, to });
      if (accepted !== false) setSelected(null);
      return accepted !== false;
    },
    [chess, onMove],
  );

  const clickSquare = useCallback(
    (sq: Square) => {
      if (pendingPromotion) return;
      if (selected && selected !== sq && targets.has(sq)) {
        tryMove(selected, sq);
        return;
      }
      if (selected === sq) {
        setSelected(null);
        return;
      }
      setSelected(canSelect(sq) ? sq : null);
    },
    [pendingPromotion, selected, targets, tryMove, canSelect],
  );

  const choosePromotion = useCallback(
    (piece: PieceSymbol | null) => {
      const p = pendingPromotion;
      setPendingPromotion(null);
      setSelected(null);
      if (p && piece) onMove?.({ from: p.from, to: p.to, promotion: piece });
    },
    [pendingPromotion, onMove],
  );

  const checkSquare = useMemo(() => {
    if (!chess.inCheck()) return null;
    return chess.findPiece({ type: "k", color: turn })[0] ?? null;
  }, [chess, turn]);

  return {
    chess,
    turn,
    selected,
    setSelected,
    targets,
    canSelect,
    clickSquare,
    tryMove,
    pendingPromotion,
    choosePromotion,
    checkSquare,
  };
}
